import { ALL_REASONS_KEY } from './text';

/**
 * @typedef {Object} AbsenceReason
 * @property {string} id
 * @property {string} name
 *
 * @typedef {Object} Absence
 * @property {string} id
 * @property {string} fromTimestamp
 * @property {string} toTimestamp
 * @property {string} createdAt
 * @property {string | null} note
 * @property {string} updatedBy
 * @property {AbsenceReason} reason
 *
 * @typedef {{ from: string, to: string }} DateRange
 * @typedef {{ reason: string, dateRange: DateRange | '' }} FilterState
 */

/**
 * Sort absences most-recent-first by `fromTimestamp`. Returns the input
 * unchanged when it is undefined (data still loading).
 *
 * @param {Absence[] | undefined} absences
 * @returns {Absence[] | undefined}
 */
export const sortedByRecently = (absences) => {
	if (absences === undefined) return absences;
	return [...absences].sort((a, b) => new Date(b.fromTimestamp) - new Date(a.fromTimestamp));
};

/**
 * Restrict `absences` to those whose `fromTimestamp` falls within
 * `dateRange.from`..`dateRange.to` (inclusive on both ends).
 *
 * @param {DateRange} dateRange
 * @param {Absence[] | undefined} absences
 * @returns {Absence[] | undefined}
 */
export const filterByDateRange = (dateRange, absences) => {
	if (absences === undefined) return absences;
	const { from, to } = dateRange;
	const startDate = new Date(from);
	const endDate = new Date(to);
	return absences.filter((absence) => {
		const fromTs = new Date(absence.fromTimestamp);
		return fromTs >= startDate && fromTs <= endDate;
	});
};

/**
 * Compose the reason + date-range filter over an absences list, returning
 * a fresh array sorted most-recent-first.
 *
 * @param {{ absences: Absence[] | undefined, filters: FilterState }} args
 * @returns {Absence[] | undefined}
 */
export const applyFilters = ({ absences, filters }) => {
	if (absences === undefined) return absences;

	const noReasonFilter = filters.reason === ALL_REASONS_KEY;
	const hasDateRange = filters.dateRange !== '';

	if (noReasonFilter && !hasDateRange) {
		return sortedByRecently(absences);
	}

	if (noReasonFilter && hasDateRange) {
		return sortedByRecently(filterByDateRange(filters.dateRange, absences));
	}

	const byReason = absences.filter((abs) => abs.reason.id === filters.reason);

	if (hasDateRange) {
		return filterByDateRange(filters.dateRange, byReason);
	}

	return sortedByRecently(byReason);
};
